import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

// Two-factor. Endpoints are unchanged from v1; this is the same flow with its own UI.
//
// The order matters and is easy to get wrong: setup returns a secret and QR, and the
// account is not actually protected until verify-setup succeeds. Anything that treats
// setup alone as "enrolled" leaves people locked out on next sign-in.
export function useMfa() {
  const [status, setStatus] = useState(null); // { method, totp_enabled }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enrolment, setEnrolment] = useState(null); // { qr, secret, otpauthUrl }
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await api.get("s1/api/mfa/status")); setError(""); }
    catch (e) { setError(e.message || "Could not read two-factor status."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const beginSetup = useCallback(async () => {
    setBusy(true); setError("");
    try { setEnrolment(await api.post("s1/api/mfa/totp/setup")); }
    catch (e) { setError(e.message || "Could not start enrolment."); }
    finally { setBusy(false); }
  }, []);

  const verifySetup = useCallback(async (code) => {
    setBusy(true); setError("");
    try {
      const data = await api.post("s1/api/mfa/totp/verify-setup", { code });
      setRecoveryCodes(data?.recoveryCodes || []);
      setEnrolment(null);
      await load();
      return true;
    } catch (e) {
      setError(e.message || "That code was not accepted. Codes expire after 30 seconds.");
      return false;
    } finally { setBusy(false); }
  }, [load]);

  const setMethod = useCallback(async (method) => {
    setBusy(true); setError("");
    try { await api.post("s1/api/mfa/method", { method }); await load(); return true; }
    catch (e) { setError(e.message || "Could not change the method."); return false; }
    finally { setBusy(false); }
  }, [load]);

  const disable = useCallback(async (password) => {
    setBusy(true); setError("");
    try { await api.post("s1/api/mfa/totp/disable", { password }); await load(); return true; }
    catch (e) { setError(e.message || "Incorrect password."); return false; }
    finally { setBusy(false); }
  }, [load]);

  return { status, loading, error, busy, enrolment, recoveryCodes, setRecoveryCodes, beginSetup, verifySetup, setMethod, disable };
}
